import { useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import BlockIcon from '@mui/icons-material/Block';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';

import { useAppContext } from '../../../utils/content.util';
import { hasPermission, Permission } from '../../../models/role.model';

export interface DealerApiAccess {
  id: string;
  name: string;
  createdAt: string;
  isDisabled?: boolean;
}

function DealerApiAccessTable({
  data,
  canManage,
  handleDisable,
  handleDelete,
}: {
  data: DealerApiAccess[];
  canManage: boolean;
  handleDisable: (id: string) => void;
  handleDelete: (id: string) => void;
}) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Actions</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No API access entries yet
              </TableCell>
            </TableRow>
          )}
          {data.map((row) => (
            <TableRow key={row.id} selected={row.isDisabled}>
              <TableCell align="left">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {canManage && (
                    <>
                      <Tooltip title="Disable">
                        <BlockIcon
                          color="warning"
                          fontSize="small"
                          onClick={() => handleDisable(row.id)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <DeleteForeverIcon
                          color="error"
                          fontSize="small"
                          onClick={() => handleDelete(row.id)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </>
                  )}
                </Box>
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{format(new Date(row.createdAt), 'MM/dd/yyyy')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function DealerApiAccessContainer() {
  const { currentUser } = useAppContext();
  const [items] = useState<DealerApiAccess[]>([]);

  const canManage = !!currentUser && hasPermission(currentUser, Permission.MANAGE_DEALER_API_ACCESS);

  // TODO: wire up to the API once the backend exists
  const handleDisable = (_id: string) => {};
  const handleDelete = (_id: string) => {};

  return (
    <Grid size={{ xs: 12 }}>
      <div className="dealer-api-access">
        <h2 className="pb-2 mt-4 mb-3 border-bottom">Dealer API Access</h2>
        <div className="pb-2 mt-4 mb-3">
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            {canManage && (
              <ReactRouterLink to="/dealer-api-access/new">
                <Button variant="contained">Add</Button>
              </ReactRouterLink>
            )}
          </Box>
        </div>
        <DealerApiAccessTable
          data={items}
          canManage={canManage}
          handleDisable={handleDisable}
          handleDelete={handleDelete}
        />
      </div>
    </Grid>
  );
}
